import {
  SideNavigationWrapper,
  HeaderRow,
  Text,
  LeftToggleButton,
  Divider,
  Section,
  Label,
  Icon,
  RowWithTooltip,
} from '../Navbar/SideBar';
import { useHistory, useParams } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import useAuth from '../../hooks/use-auth';
import { ROUTES } from '../../routing';

export enum COMPANY_PAGES {
  OVERVIEW = 'overview',
  LEARNERS = 'learners',
  COHORTS = 'cohorts',
  IMPACT = 'impact',
  MENTORS = 'mentors',
  SETTINGS = 'settings',
  ACTIVITY_FEED = 'activity-feed',
  CONTENT = 'content',
  SUBMISSIONS = 'submissions',
  OFFICE_HOURS = 'office-hours',
  PROGRESS = 'progress',
  ACTIONABLES = 'actionables',
  COMPANY_PROJECTS = 'company-projects',
  ASSESSMENT_TESTS = 'assessment-tests',
  ASSESSMENT_TEST_RESULTS = 'assessment-test-results',
  ASSESSMENT_TEST_RESULT = 'assessment-test-result',
}

export default function CompanySideNavigation({
  onClose,
  currentPage,
}: {
  onClose: () => void;
  currentPage: COMPANY_PAGES;
}) {
  const { id } = useParams<{ id: string }>();
  const { data } = useGetCompany(parseInt(id));
  const { user } = useAuth();

  const isAdmin = user?.roles.includes('ADMIN');
  const hasCompanyProjects = !!data?.settings?.COMPANY_PROJECT_UPLOAD;
  const hasAssessmentTest = !!data?.settings?.ASSESSMENT_TEST;

  const history = useHistory();

  const goToLearners = () => {
    history.push(ROUTES.ADMIN.LEARNERS.getPath({ companyId: id }));
  };

  const goToCohorts = () => {
    history.push(ROUTES.ADMIN.COHORTS.getPath({ companyId: id }));
  };

  const goToMentors = () => {
    history.push(ROUTES.ADMIN.MENTORS.getPath({ companyId: id }));
  };

  const goToSettings = () => {
    history.push(ROUTES.ADMIN.SETTINGS.getPath({ companyId: id }));
  };

  const goToSOSQuestions = () => {
    history.push(ROUTES.ADMIN.SOS_QUESTIONS.getPath({ companyId: id }));
  };

  const goToOfficeHours = () => {
    history.push(ROUTES.ADMIN.OFFICE_HOURS.getPath({ companyId: id }));
  };

  const goToProgress = () => {
    history.push(ROUTES.ADMIN.PROGRESS.getPath({ companyId: id }));
  };

  const goToCompanyProjects = () => {
    history.push(ROUTES.ADMIN.COMPANY_PROJECTS.getPath({ companyId: id }));
  };

  const goToAssessmentTests = () => {
    history.push(ROUTES.ADMIN.ASSESSMENT_TESTERS.getPath({ companyId: id }));
  };

  const goToAssessmentTestResults = () => {
    history.push(
      ROUTES.ADMIN.ASSESSMENT_TEST_RESULTS.getPath({ companyId: id })
    );
  };

  return (
    <SideNavigationWrapper>
      <HeaderRow>
        <Text style={{ fontWeight: 'bold' }}>{data?.name} Team</Text>
        <LeftToggleButton onClick={onClose} />
      </HeaderRow>
      <Divider />
      <Section>
        <Label>Monitor</Label>
        <RowWithTooltip
          id="progress"
          isActive={currentPage === COMPANY_PAGES.PROGRESS}
          onClick={goToProgress}
        >
          <Text>Learner Progress</Text>
          <Icon className="fa-solid fa-chart-simple" />
        </RowWithTooltip>
        {hasAssessmentTest && (
          <RowWithTooltip
            id="assessment-test-progress"
            isActive={currentPage === COMPANY_PAGES.ASSESSMENT_TEST_RESULTS}
            onClick={goToAssessmentTestResults}
          >
            <Text>Assessment Results</Text>
            <Icon className="fa-solid fa-radar" />
          </RowWithTooltip>
        )}
      </Section>
      <Divider />
      <Section>
        <Label>Review</Label>
        <RowWithTooltip
          isActive={currentPage === COMPANY_PAGES.SUBMISSIONS}
          onClick={goToSOSQuestions}
          id="sos-questions"
        >
          <Text>SOS Questions</Text>
          <Icon className="fa-solid fa-check-double" />
        </RowWithTooltip>
        <RowWithTooltip
          id="office-hours"
          isActive={currentPage === COMPANY_PAGES.OFFICE_HOURS}
          onClick={goToOfficeHours}
        >
          <Text>Office Hours</Text>
          <Icon className="fa-solid fa-clock" />
        </RowWithTooltip>
        {hasCompanyProjects && (
          <RowWithTooltip
            id="company-projects"
            isActive={currentPage === COMPANY_PAGES.COMPANY_PROJECTS}
            onClick={goToCompanyProjects}
          >
            <Text>Company Projects</Text>
            <Icon className="fa-solid fa-arrow-up-from-bracket" />
          </RowWithTooltip>
        )}
      </Section>
      <Divider />
      <Section>
        <Label>Manage</Label>
        <RowWithTooltip
          id="learners"
          tooltip={isAdmin ? '' : 'Only available to admins'}
          isActive={currentPage === COMPANY_PAGES.LEARNERS}
          onClick={goToLearners}
          isDisabled={!isAdmin}
        >
          <Text>Learners</Text>
          <Icon className="fa-solid fa-users" />
        </RowWithTooltip>
        <RowWithTooltip
          id="cohorts"
          tooltip={isAdmin ? '' : 'Only available to admins'}
          isActive={currentPage === COMPANY_PAGES.COHORTS}
          onClick={goToCohorts}
          isDisabled={!isAdmin}
        >
          <Text>Cohorts</Text>
          <Icon className="fa-solid fa-calendar-days" />
        </RowWithTooltip>

        <RowWithTooltip
          id="mentors"
          tooltip={isAdmin ? '' : 'Only available to admins'}
          isActive={currentPage === COMPANY_PAGES.MENTORS}
          onClick={goToMentors}
          isDisabled={!isAdmin}
        >
          <Text>Mentors</Text>
          <Icon className="fa-solid fa-chalkboard-user" />
        </RowWithTooltip>
        {hasAssessmentTest && (
          <RowWithTooltip
            id="assessment-tests"
            tooltip={isAdmin ? '' : 'Only available to admins'}
            isActive={currentPage === COMPANY_PAGES.ASSESSMENT_TESTS}
            onClick={goToAssessmentTests}
            isDisabled={!isAdmin}
          >
            <Text>Testers</Text>
            <Icon className="fa-solid fa-list-check" />
          </RowWithTooltip>
        )}
        <RowWithTooltip
          id="settings"
          tooltip={isAdmin ? '' : 'Only available to admins'}
          isActive={currentPage === COMPANY_PAGES.SETTINGS}
          onClick={goToSettings}
          isDisabled={!isAdmin}
        >
          <Text>Settings</Text>
          <Icon className="fa-solid fa-gear" />
        </RowWithTooltip>
      </Section>
    </SideNavigationWrapper>
  );
}
